import { useCallback, useEffect, useRef, useState } from 'react'
import type { AuthUser } from '../contexts/AuthContext'
import { getApiErrorMessage } from '../helpers/ApiErrorMessage'
import { noraApi, type NoraConversation, type NoraMessage } from '../helpers/NoraApi'

const INITIAL_SUGGESTIONS = [
  'Como revisar a rotulagem do meu produto?',
  'Quais normas se aplicam ao meu alimento?',
  'Como declarar os alergênicos no rótulo?'
]

function remember (key: string, id: string): void {
  try { window.localStorage.setItem(key, id) } catch {}
}

function storedConversation (key: string): string | null {
  try { return window.localStorage.getItem(key) } catch { return null }
}

interface NoraChatState {
  conversations: NoraConversation[]
  selectedId: string | null
  messages: NoraMessage[]
  draft: string
  suggestions: string[]
  isLoading: boolean
  isSending: boolean
  isLoadingOlder: boolean
  hasMoreConversations: boolean
  hasMoreMessages: boolean
  error: string
  setDraft: (value: string) => void
  loadConversation: (id: string) => Promise<void>
  newConversation: () => Promise<void>
  send: (suggestion?: string) => Promise<void>
  stop: () => void
  loadOlder: (kind: 'conversations' | 'messages') => Promise<void>
  reload: () => void
}

export function useNoraChat (active: boolean, user: AuthUser | null, initialPrompt?: string): NoraChatState {
  const [conversations, setConversations] = useState<NoraConversation[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [messages, setMessages] = useState<NoraMessage[]>([])
  const [draft, setDraft] = useState('')
  const [suggestions, setSuggestions] = useState(INITIAL_SUGGESTIONS)
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const [hasMoreConversations, setHasMoreConversations] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [error, setError] = useState('')
  const [pendingPrompt, setPendingPrompt] = useState('')
  const [reloadKey, setReloadKey] = useState(0)
  const generation = useRef(0)
  const requestRef = useRef<AbortController | null>(null)
  const streamRef = useRef<AbortController | null>(null)
  const busyRef = useRef(false)
  const storageKey = useRef('')
  const consumedPrompt = useRef('')
  const conversationOffset = useRef(0)
  const uncertainTurn = useRef<{ conversationId: string, operationId: string, question: string } | null>(null)

  const loadConversation = useCallback(async (id: string) => {
    if (busyRef.current) return
    const revision = ++generation.current
    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    setIsLoading(true)
    setError('')
    try {
      const data = await noraApi.getMessages(id, undefined, controller.signal)
      if (generation.current !== revision) return
      setSelectedId(id)
      uncertainTurn.current = null
      setMessages(data.messages)
      setDraft('')
      setSuggestions(INITIAL_SUGGESTIONS)
      setHasMoreMessages(data.hasMore)
      setConversations(current => current.some(item => item.id === id) ? current : [data.conversation, ...current])
      remember(storageKey.current, id)
    } catch (error) {
      if (!controller.signal.aborted && generation.current === revision) setError(getApiErrorMessage(error, 'Não foi possível carregar a conversa.'))
    } finally {
      if (generation.current === revision) setIsLoading(false)
    }
  }, [])

  const newConversation = useCallback(async () => {
    if (busyRef.current) return
    busyRef.current = true
    const revision = ++generation.current
    requestRef.current?.abort()
    setIsLoading(true)
    setError('')
    try {
      const conversation = await noraApi.createConversation()
      if (generation.current !== revision) return
      setConversations(current => [conversation, ...current])
      conversationOffset.current++
      setSelectedId(conversation.id)
      setMessages([])
      setDraft('')
      setSuggestions(INITIAL_SUGGESTIONS)
      setHasMoreMessages(false)
      remember(storageKey.current, conversation.id)
    } catch (error) {
      if (generation.current === revision) setError(getApiErrorMessage(error, 'Não foi possível criar a conversa.'))
    } finally {
      if (generation.current === revision) { busyRef.current = false; setIsLoading(false) }
    }
  }, [])

  const send = useCallback(async (suggestion?: string) => {
    const question = (suggestion ?? draft).trim()
    if (!question || busyRef.current || isLoading) return
    if (question.length > 1000) {
      setDraft(question.slice(0, 1000))
      setError('A pergunta pode ter até 1.000 caracteres. Revise o texto antes de enviar.')
      return
    }
    busyRef.current = true
    const revision = ++generation.current
    requestRef.current?.abort()
    const controller = new AbortController()
    streamRef.current = controller
    let operationId: string = crypto.randomUUID()
    const createdAt = new Date().toISOString()
    setError('')
    setDraft('')
    setSuggestions([])
    setIsSending(true)
    try {
      let id = selectedId
      if (id && uncertainTurn.current?.conversationId === id) {
        const uncertain = uncertainTurn.current
        const history = await noraApi.getMessages(id)
        if (generation.current !== revision) return
        const recovered = history.messages.find(item => item.turnId === uncertain.operationId && item.role === 'assistant' && !item.state)
        if (recovered) {
          setMessages(history.messages)
          uncertainTurn.current = null
          if (question === uncertain.question) return
        } else {
          const failed = history.messages.some(item => item.turnId === uncertain.operationId && item.turnStatus === 'failed')
          if (!failed && question === uncertain.question) operationId = uncertain.operationId
        }
      }
      if (!id) {
        const conversation = await noraApi.createConversation()
        if (generation.current !== revision) return
        id = conversation.id
        setConversations(current => [conversation, ...current])
        conversationOffset.current++
        setSelectedId(id)
        remember(storageKey.current, id)
      }
      uncertainTurn.current = { conversationId: id, operationId, question }
      setMessages(current => [...current.filter(item => item.turnId !== operationId),
        { id: `${operationId}:user`, turnId: operationId, role: 'user', content: question, createdAt },
        { id: `${operationId}:assistant`, turnId: operationId, role: 'assistant', content: '', createdAt, state: 'streaming' }
      ])
      const completed = await noraApi.send(id, question, operationId, text => {
        if (generation.current !== revision) return
        setMessages(current => current.map(item => item.id === `${operationId}:assistant`
          ? { ...item, content: item.content + text }
          : item))
      }, controller.signal)
      if (generation.current !== revision) return
      uncertainTurn.current = null
      setMessages(current => current.map(item => item.id === `${operationId}:assistant` ? completed : item))
      setConversations(current => current.map(item => item.id === id
        ? { ...item, title: item.title === 'Nova conversa' ? question.slice(0, 100) : item.title, updatedAt: new Date().toISOString() }
        : item)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)))
      const suggestionsController = new AbortController()
      requestRef.current = suggestionsController
      void noraApi.suggestions(completed.turnId, suggestionsController.signal).then(items => {
        if (generation.current === revision) setSuggestions(items)
      }).catch(() => {})
    } catch (error) {
      if (generation.current !== revision) return
      const notice = controller.signal.aborted ? 'Resposta interrompida.' : getApiErrorMessage(error, 'Não foi possível responder. Tente novamente.')
      setError(notice)
      setMessages(current => current.map(item => item.id === `${operationId}:assistant`
        ? { ...item, content: item.content || notice, state: 'failed' }
        : item))
      setDraft(question)
    } finally {
      if (generation.current === revision) { busyRef.current = false; setIsSending(false) }
    }
  }, [draft, isLoading, selectedId])

  const loadOlder = useCallback(async (kind: 'conversations' | 'messages') => {
    if (isLoadingOlder || busyRef.current || isLoading) return
    const revision = generation.current
    setIsLoadingOlder(true)
    try {
      if (kind === 'conversations') {
        const data = await noraApi.listConversations(conversationOffset.current)
        if (generation.current !== revision) return
        conversationOffset.current += data.conversations.length
        setConversations(current => [...current, ...data.conversations.filter(item => !current.some(existing => existing.id === item.id))])
        setHasMoreConversations(data.hasMore)
      } else if (selectedId) {
        const data = await noraApi.getMessages(selectedId, messages[0]?.createdAt)
        if (generation.current !== revision) return
        setMessages(current => [...data.messages.filter(item => !current.some(existing => existing.id === item.id)), ...current])
        setHasMoreMessages(data.hasMore)
      }
    } catch (error) {
      if (generation.current === revision) setError(getApiErrorMessage(error, 'Não foi possível carregar o histórico.'))
    } finally { setIsLoadingOlder(false) }
  }, [isLoadingOlder, isLoading, selectedId, messages.length])

  const stop = useCallback(() => { streamRef.current?.abort() }, [])

  useEffect(() => {
    const revision = ++generation.current
    requestRef.current?.abort()
    streamRef.current?.abort()
    busyRef.current = false
    setIsSending(false)
    setDraft('')
    setSuggestions(INITIAL_SUGGESTIONS)
    setMessages([])
    setConversations([])
    setSelectedId(null)
    setPendingPrompt('')
    if (!active || !user) return
    const controller = new AbortController()
    requestRef.current = controller
    setIsLoading(true)
    setError('')
    setConversations([])
    setMessages([])
    setSelectedId(null)
    void noraApi.listConversations(0, controller.signal).then(async data => {
      if (generation.current !== revision) return
      storageKey.current = `forpy:nora:active:${data.accountId}:${data.userId}`
      conversationOffset.current = data.conversations.length
      setConversations(data.conversations)
      setHasMoreConversations(data.hasMore)
      if (initialPrompt?.trim() && consumedPrompt.current !== initialPrompt) {
        consumedPrompt.current = initialPrompt
        setPendingPrompt(initialPrompt.trim())
        setIsLoading(false)
        return
      }
      const id = storedConversation(storageKey.current) ?? data.conversations[0]?.id
      if (id) {
        try {
          const history = await noraApi.getMessages(id, undefined, controller.signal)
          if (generation.current !== revision) return
          setSelectedId(id)
          setMessages(history.messages)
          setHasMoreMessages(history.hasMore)
          setConversations(current => current.some(item => item.id === id) ? current : [history.conversation, ...current])
          remember(storageKey.current, id)
        } catch (error) {
          if (!controller.signal.aborted && generation.current === revision) setError(getApiErrorMessage(error, 'Não foi possível recuperar a conversa. Selecione outra ou crie uma nova.'))
        }
      }
      if (generation.current === revision) setIsLoading(false)
    }).catch(error => {
      if (!controller.signal.aborted && generation.current === revision) {
        setError(getApiErrorMessage(error, 'Não foi possível carregar a Nora.'))
        setIsLoading(false)
      }
    })
    return () => { generation.current++; controller.abort(); requestRef.current?.abort(); streamRef.current?.abort() }
  }, [active, user, initialPrompt, reloadKey])

  useEffect(() => {
    if (pendingPrompt && !isLoading && active) {
      setPendingPrompt('')
      void send(pendingPrompt)
    }
  }, [pendingPrompt, isLoading, active, send])

  return {
    conversations,
    selectedId,
    messages,
    draft,
    suggestions,
    isLoading,
    isSending,
    isLoadingOlder,
    hasMoreConversations,
    hasMoreMessages,
    error,
    setDraft,
    loadConversation,
    newConversation,
    send,
    stop,
    loadOlder,
    reload: () => setReloadKey(value => value + 1)
  }
}
